<script setup lang="ts">
    import { computed } from 'vue'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        propValue?: number | null
        propComment?: string | null
        propFirstAlternativeName: string
        propSecondAlternativeName: string
    }>()

    const emit = defineEmits<{
        (e: 'update-value', value: number | null): void
        (e: 'update-comment', comment: string): void
    }>()

    /* ---------------- derived ---------------- */

    const value = computed<number | null>(() => props.propValue ?? null)
    const comment = computed<string | null>(() => props.propComment ?? null)

    const firstAlternativeName = computed(() => props.propFirstAlternativeName)
    const secondAlternativeName = computed(() => props.propSecondAlternativeName)

    const FirstItemSelected = computed(() => value.value != null && value.value > 1.0)
    const SecondItemSelected = computed(() => value.value != null && value.value < 1.0)
    const EqualitySelected = computed(() => value.value === 1.0)

    /* ---------------- labels ---------------- */

    const ScaleLabel = computed<string | null>(() =>
    {
        if (value.value == null) return null

        let v = value.value
        if (v < 1.0)
        {
            v = Math.round((1 / v) * 100) / 100
        }

        switch (v)
        {
            case 2.0: return 'Weakly'
            case 3.0: return 'Moderately'
            case 4.0: return 'Moderately +'
            case 5.0: return 'Strongly'
            case 6.0: return 'Strongly +'
            case 7.0: return 'Very Strongly'
            case 8.0: return 'Very Strongly +'
            case 9.0: return 'Extremely'
            default: return ''
        }
    })

    /* ---------------- actions ---------------- */

    function updateValue(v: number | null)
    {
        emit('update-value', v)
    }

    function updateComment(c: string)
    {
        emit('update-comment', c)
    }

    function SetEqualCommand()
    {
        if (EqualitySelected.value === true)
        {
            updateValue(null)
        } else
        {
            updateValue(1.0)
        }
    }
</script>


<template>
    <div>
        <div class="btn-group-vertical d-flex" role="group">
            <div class="btn-group" role="group">
                <button type="button" data-toggle="dropdown"
                        class="btn text-left" :class="FirstItemSelected === true ? 'btn-primary' : 'btn-outline-secondary'"
                        style="padding: 0.75rem 1.25rem;">

                    <i class="fa fa-circle-thin" aria-hidden="true" v-if="FirstItemSelected === true"></i>

                    <span class="badge badge-pill badge-light text-primary" v-if="FirstItemSelected === true">
                        <i class="fa fa-check" aria-hidden="true"></i> {{ScaleLabel}}
                    </span>

                    {{firstAlternativeName}}
                </button>
                <div class="dropdown-menu">
                    <h4 class="dropdown-header">How strong is your preference?</h4>
                    <div class="dropdown-divider"></div>
                    <a class="dropdown-item" @click="updateValue(2)">Weak</a>
                    <a class="dropdown-item" @click="updateValue(3)">Moderate</a>
                    <a class="dropdown-item" @click="updateValue(4)">Moderate +</a>
                    <a class="dropdown-item" @click="updateValue(5)">Strong</a>
                    <a class="dropdown-item" @click="updateValue(6)">Strong +</a>
                    <a class="dropdown-item" @click="updateValue(7)">Very strong</a>
                    <a class="dropdown-item" @click="updateValue(8)">Very strong +</a>
                    <a class="dropdown-item" @click="updateValue(9)">Extreme</a>
                    <template v-if="FirstItemSelected === true">
                        <div class="dropdown-divider"></div>
                        <a class="dropdown-item" @click="updateValue(null)">
                            <i class="fa fa-times text-danger" aria-hidden="true"></i>
                            Clear
                        </a>
                    </template>
                </div>
            </div>

            <button type="button" @click="SetEqualCommand" class="btn text-left" :class="EqualitySelected === true ? 'btn-primary' : 'btn-outline-secondary'" style="padding: 0.75rem 1.25rem;">
                <i class="fa" :class="EqualitySelected === true ? 'fa-check-circle' : 'fa-circle-thin'"></i>
                Equally preferred
            </button>

            <div class="btn-group" role="group">
                <button type="button" class="btn text-left" data-toggle="dropdown" :class="SecondItemSelected === true ? 'btn-primary' : 'btn-outline-secondary'" style="padding: 0.75rem 1.25rem;">
                    <i class="fa fa-circle-thin" aria-hidden="true" v-if="SecondItemSelected === true"></i>

                    <span class="badge badge-pill badge-light text-primary" v-if="SecondItemSelected === true">
                        <i class="fa fa-check" aria-hidden="true"></i> {{ScaleLabel}}
                    </span>

                    {{secondAlternativeName}}
                </button>
                <div class="dropdown-menu">
                    <h4 class="dropdown-header">How strong is your preference?</h4>
                    <div class="dropdown-divider"></div>
                    <a class="dropdown-item" @click="updateValue(1.0/2.0)">Weak</a>
                    <a class="dropdown-item" @click="updateValue(1.0/3.0)">Moderate</a>
                    <a class="dropdown-item" @click="updateValue(1.0/4.0)">Moderate +</a>
                    <a class="dropdown-item" @click="updateValue(1.0/5.0)">Strong</a>
                    <a class="dropdown-item" @click="updateValue(1.0/6.0)">Strong +</a>
                    <a class="dropdown-item" @click="updateValue(1.0/7.0)">Very strong</a>
                    <a class="dropdown-item" @click="updateValue(1.0/8.0)">Very strong +</a>
                    <a class="dropdown-item" @click="updateValue(1.0/9.0)">Extreme</a>
                    <template v-if="SecondItemSelected === true">
                        <div class="dropdown-divider"></div>
                        <a class="dropdown-item" @click="updateValue(null)">
                            <i class="fa fa-times text-danger" aria-hidden="true"></i>
                            Clear
                        </a>
                    </template>
                </div>
            </div>
        </div>
        <textarea rows="3" :value.trim="comment" @input="updateComment($event.target.value)" class="form-control my-3" placeholder="Comment..."></textarea>
    </div>
</template>